import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  FilterBar,
  KpiCard,
  ListArchetypeToolbar,
  PageHeader,
  useListArchetype,
  type FilterChipDef,
  downloadCsv,
} from '@tyndo/ui-kit';
import { usePlantDateFormat } from '@tyndo/hooks';
import { CheckSquare, Inbox, RefreshCw } from 'lucide-react';
import type {
  ApprovalPriority,
  ApprovalQueueItem,
  ApprovalQueueProvider,
} from '@tyndo/types';
import { approvalProviderRegistry } from '../registries';
import { ModuleShell } from '../layout/ModuleView';

/**
 * "My Approvals" — the shell's cross-module approval inbox (UI-3).
 *
 * Tyndo scatters ~14 per-module approval queues (Sales discount/contract
 * approvals, Compliance change-control review, HR leave requests, Quality NCR
 * dispositions, …). This surface AGGREGATES every module that has registered an
 * {@link ApprovalQueueProvider} into ONE prioritized, module-groupable queue.
 * Each row deep-links to the owning module's native surface for the actual
 * approve/reject (safest: the approval e-signature / disposition flows already
 * live there and stay the single source of truth).
 *
 * Each provider's `useApprovals` is a React hook. To honour the Rules of Hooks
 * with a dynamic provider set, we render one {@link ProviderSlot} per registered
 * provider (the set is stable after module bootstrap) — each slot calls its
 * hook unconditionally and reports its slice up. The parent merges the slices.
 */

const PRIORITY_RANK: Record<ApprovalPriority, number> = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3,
};

const PRIORITY_BADGE: Record<
  ApprovalPriority,
  'critical' | 'warning' | 'info' | 'muted'
> = {
  critical: 'critical',
  high: 'warning',
  normal: 'info',
  low: 'muted',
};

interface ProviderSlice {
  moduleId: string;
  items: ApprovalQueueItem[];
  isLoading: boolean;
  isError: boolean;
  refetch?: () => void;
}

/**
 * Renders (invisibly) a single provider: calls its `useApprovals` hook and
 * pushes the resulting slice up to the parent. One slot per provider keeps every
 * hook call unconditional and stable in order.
 */
function ProviderSlot({
  provider,
  onSlice,
}: {
  provider: ApprovalQueueProvider;
  onSlice: (slice: ProviderSlice) => void;
}) {
  const { items, isLoading, isError, refetch } = provider.useApprovals();

  useEffect(() => {
    onSlice({ moduleId: provider.moduleId, items, isLoading, isError, refetch });
    // `items` identity changes when the underlying query resolves; report then.
  }, [provider.moduleId, items, isLoading, isError, refetch, onSlice]);

  return null;
}

export default function MyApprovals() {
  const navigate = useNavigate();
  const fmtPlantDate = usePlantDateFormat({ dateStyle: 'medium' });

  // Snapshot the registered providers, re-reading when the registry mutates
  // (a module registering its provider during the federated Pass 2 must light
  // up this surface even if it mounted first).
  const [providers, setProviders] = useState<ApprovalQueueProvider[]>(() =>
    approvalProviderRegistry.list(),
  );
  useEffect(() => {
    // Re-sync immediately in case a provider registered between the initial
    // render and the subscription, then on every subsequent registry change.
    setProviders(approvalProviderRegistry.list());
    return approvalProviderRegistry.subscribe(() =>
      setProviders(approvalProviderRegistry.list()),
    );
  }, []);

  // Per-provider slices, keyed by moduleId. Updated as each provider's query
  // resolves.
  const [slices, setSlices] = useState<Record<string, ProviderSlice>>({});
  const handleSlice = useCallback((slice: ProviderSlice) => {
    setSlices((prev) => {
      const existing = prev[slice.moduleId];
      if (
        existing &&
        existing.isLoading === slice.isLoading &&
        existing.isError === slice.isError &&
        existing.items === slice.items
      ) {
        return prev;
      }
      return { ...prev, [slice.moduleId]: slice };
    });
  }, []);

  // Drop slices for providers that are no longer registered (tenant switch).
  useEffect(() => {
    setSlices((prev) => {
      const live = new Set(providers.map((p) => p.moduleId));
      const next: Record<string, ProviderSlice> = {};
      let changed = false;
      for (const [id, slice] of Object.entries(prev)) {
        if (live.has(id)) next[id] = slice;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [providers]);

  const moduleLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of providers) m.set(p.moduleId, p.moduleLabel);
    return m;
  }, [providers]);

  const allItems = useMemo(() => {
    const merged: ApprovalQueueItem[] = [];
    for (const p of providers) {
      const slice = slices[p.moduleId];
      if (slice) merged.push(...slice.items);
    }
    // Prioritized queue: urgency band first, then oldest-waiting first.
    return merged.sort((a, b) => {
      const pa = PRIORITY_RANK[a.priority ?? 'normal'];
      const pb = PRIORITY_RANK[b.priority ?? 'normal'];
      if (pa !== pb) return pa - pb;
      const ta = a.requestedAt ? Date.parse(a.requestedAt) : Number.POSITIVE_INFINITY;
      const tb = b.requestedAt ? Date.parse(b.requestedAt) : Number.POSITIVE_INFINITY;
      return ta - tb;
    });
  }, [providers, slices]);

  const anyLoading = providers.some((p) => slices[p.moduleId]?.isLoading ?? true);
  // Errored only when EVERY provider that reported has errored (a single module
  // being down must not blank the whole inbox).
  const reported = providers
    .map((p) => slices[p.moduleId])
    .filter((s): s is ProviderSlice => Boolean(s));
  const allErrored =
    reported.length > 0 && reported.every((s) => s.isError);

  // Module filter (chip) + list archetype (toolbar affordances).
  const [moduleFilter, setModuleFilter] = useState<string | null>(null);
  const archetype = useListArchetype({
    viewId: 'shell.my-approvals',
    permission: { resource: 'approvals', action: 'read' },
    savedFilters: [
      { id: 'critical', label: 'Critical only' },
      { id: 'high', label: 'High & up' },
    ],
    columns: [
      { id: 'title', label: 'Item', required: true },
      { id: 'module', label: 'Module', defaultVisible: true },
      { id: 'requestedBy', label: 'Requested by', defaultVisible: true },
      { id: 'priority', label: 'Priority', defaultVisible: true },
    ],
    defaultPageSize: 50,
  });

  const visibleItems = useMemo(() => {
    let rows = allItems;
    if (moduleFilter) rows = rows.filter((r) => r.moduleId === moduleFilter);
    // The archetype's saved-filter chips narrow by priority band.
    if (archetype.activeFilterId === 'critical') {
      rows = rows.filter((r) => (r.priority ?? 'normal') === 'critical');
    } else if (archetype.activeFilterId === 'high') {
      rows = rows.filter((r) =>
        ['critical', 'high'].includes(r.priority ?? 'normal'),
      );
    }
    return rows;
  }, [allItems, moduleFilter, archetype.activeFilterId]);

  const refetchAll = useCallback(() => {
    for (const s of Object.values(slices)) s.refetch?.();
  }, [slices]);

  const filterChips = useMemo<FilterChipDef[]>(() => {
    if (!moduleFilter) return [];
    return [
      {
        id: 'module',
        label: 'Module',
        value: moduleLabels.get(moduleFilter) ?? moduleFilter,
        onClear: () => setModuleFilter(null),
      },
    ];
  }, [moduleFilter, moduleLabels]);

  const availableModuleFilters = useMemo(
    () =>
      providers
        .filter((p) => p.moduleId !== moduleFilter)
        .map((p) => ({
          id: p.moduleId,
          label: p.moduleLabel,
          onSelect: () => setModuleFilter(p.moduleId),
        })),
    [providers, moduleFilter],
  );

  // Per-module pending counts for the KPI strip.
  const countsByModule = useMemo(() => {
    const m = new Map<string, number>();
    for (const item of allItems) {
      m.set(item.moduleId, (m.get(item.moduleId) ?? 0) + 1);
    }
    return m;
  }, [allItems]);

  return (
    <ModuleShell>
      {/* Hidden provider runners — each calls its module's approval hook. */}
      {providers.map((p) => (
        <ProviderSlot key={p.moduleId} provider={p} onSlice={handleSlice} />
      ))}

      <PageHeader
        title="My Approvals"
        subtitle={
          providers.length === 0
            ? 'No modules have registered an approval queue yet.'
            : `${allItems.length} pending across ${providers.length} module${
                providers.length === 1 ? '' : 's'
              }`
        }
        icon={CheckSquare}
        actions={[
          {
            label: 'Refresh',
            icon: RefreshCw,
            onClick: refetchAll,
            variant: 'secondary',
          },
        ]}
      />

      {providers.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {providers.map((p) => (
            <KpiCard
              key={p.moduleId}
              title={p.moduleLabel}
              value={countsByModule.get(p.moduleId) ?? 0}
              icon={Inbox}
            />
          ))}
        </div>
      )}

      <Card variant="elevated" className="overflow-hidden">
        <ListArchetypeToolbar
          archetype={archetype}
          totalCount={visibleItems.length}
          onExport={(format) => {
            if (format === 'json') {
              const blob = new Blob([JSON.stringify(visibleItems, null, 2)], {
                type: 'application/json',
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'my-approvals.json';
              a.click();
              URL.revokeObjectURL(url);
              return;
            }
            downloadCsv(visibleItems, {
              filename: 'my-approvals',
              columns: [
                { header: 'Module', value: (r) => r.moduleLabel },
                { header: 'Item', value: (r) => r.title ?? '' },
                { header: 'RequestedBy', value: (r) => r.requestedBy ?? '' },
                { header: 'Priority', value: (r) => r.priority ?? 'normal' },
              ],
            });
          }}
        />

        <FilterBar
          filters={filterChips}
          available={availableModuleFilters}
          savedViewsSlot={archetype.savedViewsSlot}
        />

        <div className="p-3">
          {allErrored ? (
            <ErrorState
              title="Couldn't load approvals"
              message="Every registered approval queue failed to load. Check your connection and try again."
            />
          ) : providers.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="No approval queues registered"
              description="Modules that expose an approval queue will appear here automatically once they load."
            />
          ) : visibleItems.length === 0 ? (
            <EmptyState
              icon={CheckSquare}
              title={anyLoading ? 'Loading approvals…' : 'Nothing waiting on you'}
              description={
                anyLoading
                  ? 'Fetching pending approvals from each module.'
                  : moduleFilter || archetype.activeFilterId
                    ? 'No approvals match the current filter.'
                    : 'You have no pending approvals across any module. Nice work.'
              }
              compact
            />
          ) : (
            <ul className="divide-y divide-border">
              {visibleItems.map((item) => {
                const priority = item.priority ?? 'normal';
                return (
                  <li key={`${item.moduleId}:${item.id}`}>
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={() => navigate(item.link)}
                      className="flex w-full items-start justify-start h-auto rounded-none whitespace-normal gap-3 px-2 py-3 text-left transition-colors hover:bg-accent/50"
                    >
                      <Badge variant={PRIORITY_BADGE[priority]} className="mt-0.5 shrink-0 capitalize">
                        {priority}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-foreground">
                            {item.title}
                          </span>
                          <Badge variant="muted" className="shrink-0 text-[10px]">
                            {item.moduleLabel}
                          </Badge>
                        </div>
                        {item.subtitle && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {item.subtitle}
                          </p>
                        )}
                        {(item.requestedBy || item.requestedAt) && (
                          <p className="mt-0.5 text-[11px] text-muted-foreground/80">
                            {item.requestedBy ? `Requested by ${item.requestedBy}` : ''}
                            {item.requestedBy && item.requestedAt ? ' · ' : ''}
                            {item.requestedAt ? fmtPlantDate(item.requestedAt) : ''}
                          </p>
                        )}
                      </div>
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Card>
    </ModuleShell>
  );
}
